import { useState, lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';

const isDevAuth = import.meta.env.VITE_AUTH_MODE === 'dev';

// Lazy-load Clerk's PricingTable only in non-dev mode to avoid initialising
// Clerk in the dev environment where ClerkProvider is not mounted.
const ClerkPricingTable = !isDevAuth
  ? lazy(() =>
      import('@clerk/clerk-react').then((m) => ({ default: m.PricingTable }))
    )
  : null;

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------

function MenuIcon() {
  return (
    <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className ?? 'h-5 w-5'}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" strokeWidth={1.5} />
      <path d="M8 12l3 3 5-5" />
    </svg>
  );
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className ?? 'h-5 w-5'} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------

const freeFeatures = [
  '1 person in your care',
  'Up to 3 family members',
  'Tasks, medications & appointments',
  'Emergency profiles',
  '30 days of activity history',
];

const familyFeatures = [
  'Everything in Free',
  'Unlimited people in care',
  'Unlimited family members',
  'Custody & handover tracking',
  'Activity scheduling',
  'Full activity history',
  'Priority support',
];

const faqs = [
  {
    question: 'Is there really a free plan?',
    answer:
      'Yes, free is genuinely free forever. No trials, no credit card required. Families with one person in care and up to three family members can use KinCare at no cost indefinitely.',
  },
  {
    question: 'Can my whole family share one subscription?',
    answer:
      'Yes. One KinCare Family subscription covers unlimited family members — everyone shares the same plan. There is no per-seat cost.',
  },
  {
    question: 'What if my needs change?',
    answer:
      'You can upgrade or downgrade at any time. If you downgrade, you keep full access until the end of your current billing period.',
  },
  {
    question: 'Do you offer a money-back guarantee?',
    answer:
      'Yes. If you are not happy for any reason, contact us within 30 days and we will refund your payment in full, no questions asked.',
  },
];

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function BillingToggle({
  billingCycle,
  onChange,
}: {
  billingCycle: 'monthly' | 'annual';
  onChange: (cycle: 'monthly' | 'annual') => void;
}) {
  return (
    <div className="inline-flex items-center gap-1 bg-cream-100 rounded-lg p-1" role="group" aria-label="Billing cycle">
      <button
        type="button"
        onClick={() => onChange('monthly')}
        className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
          billingCycle === 'monthly'
            ? 'bg-white text-gray-900 shadow-sm'
            : 'text-gray-600 hover:text-gray-900'
        }`}
      >
        Monthly
      </button>
      <button
        type="button"
        onClick={() => onChange('annual')}
        className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
          billingCycle === 'annual'
            ? 'bg-white text-gray-900 shadow-sm'
            : 'text-gray-600 hover:text-gray-900'
        }`}
      >
        Annual
        <span className="inline-block bg-accent-100 text-accent-700 text-xs font-semibold px-2 py-0.5 rounded-full">
          Save 33%
        </span>
      </button>
    </div>
  );
}

function FaqItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="border-b border-gray-200 last:border-0">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-5 text-left gap-4"
        aria-expanded={open}
      >
        <span className="text-base font-medium text-gray-900">{question}</span>
        <ChevronDownIcon
          className={`h-5 w-5 flex-shrink-0 text-gray-500 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <p className="pb-5 text-sm text-gray-600 leading-relaxed">{answer}</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function PricingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('annual');

  return (
    <div className="min-h-screen bg-cream-100">
      {/* Skip to content */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[60] focus:bg-white focus:px-4 focus:py-2 focus:rounded-lg focus:shadow-lg focus:text-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
      >
        Skip to content
      </a>

      {/* -----------------------------------------------------------------
          Header
      ----------------------------------------------------------------- */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-sm border-b border-cream-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2">
              <span className="font-fraunces text-2xl font-light tracking-tight text-navy-mid">
                <em className="font-medium text-primary-500" style={{ fontStyle: 'italic' }}>Kin</em>Care
              </span>
              <span className="hidden sm:inline text-sm text-gray-500">Care together.</span>
            </Link>

            {/* Desktop nav */}
            <nav className="hidden md:flex items-center gap-8" aria-label="Main navigation">
              <Link to="/#features" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">
                Features
              </Link>
              <Link to="/#how-it-works" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">
                How It Works
              </Link>
              <Link to="/pricing" className="text-sm text-gray-900 font-medium transition-colors">
                Pricing
              </Link>
            </nav>

            {/* Desktop CTA */}
            <div className="hidden md:flex items-center gap-3">
              <Link to="/login" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">
                Sign In
              </Link>
              <Link to="/signup" className="btn btn-accent text-sm">
                Get Started
              </Link>
            </div>

            {/* Mobile menu toggle */}
            <button
              className="md:hidden p-2 text-gray-600 hover:text-gray-900"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-menu"
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div id="mobile-menu" className="md:hidden bg-white border-t border-cream-200">
            <div className="px-4 py-4 space-y-3">
              <Link
                to="/#features"
                className="block text-sm text-gray-600 hover:text-gray-900 py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Features
              </Link>
              <Link
                to="/#how-it-works"
                className="block text-sm text-gray-600 hover:text-gray-900 py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                How It Works
              </Link>
              <Link
                to="/pricing"
                className="block text-sm text-gray-900 font-medium py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Pricing
              </Link>
              <hr className="border-cream-200" />
              <Link to="/login" className="block text-sm text-gray-600 hover:text-gray-900 py-2">
                Sign In
              </Link>
              <Link to="/signup" className="block btn btn-accent text-sm text-center">
                Get Started
              </Link>
            </div>
          </div>
        )}
      </header>

      <main id="main-content">
        {/* ---------------------------------------------------------------
            Hero
        --------------------------------------------------------------- */}
        <section className="py-16 sm:py-20 lg:py-24 bg-cream-50">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 leading-tight">
              Simple, honest pricing
            </h1>
            <p className="mt-4 text-lg sm:text-xl text-gray-600">
              Free to start. £5.99/month when you need more.
            </p>
          </div>
        </section>

        {/* ---------------------------------------------------------------
            Pricing cards
        --------------------------------------------------------------- */}
        <section className="pb-16 sm:pb-20 lg:pb-24 bg-cream-50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            {!isDevAuth && ClerkPricingTable ? (
              /* ── Production: Clerk-managed plans ── */
              <>
                <Suspense
                  fallback={
                    <div className="text-center py-12 text-gray-400 text-sm">
                      Loading plans…
                    </div>
                  }
                >
                  <ClerkPricingTable
                    appearance={{
                      variables: {
                        colorPrimary: '#547256',
                        borderRadius: '0.75rem',
                      },
                    }}
                    newSubscriptionRedirectUrl="/dashboard"
                  />
                </Suspense>
                <p className="text-center text-sm text-gray-500 mt-6">
                  No credit card required. Cancel anytime.
                </p>
              </>
            ) : (
              /* ── Dev mode fallback: static cards ── */
              <>
                {/* Billing toggle */}
                <div className="flex justify-center mb-10">
                  <BillingToggle billingCycle={billingCycle} onChange={setBillingCycle} />
                </div>

                {/* Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 items-start">
                  {/* Free card */}
                  <div className="bg-white border border-gray-200 rounded-2xl p-8">
                    <div className="mb-6">
                      <h2 className="text-xl font-bold text-gray-900">Free</h2>
                      <p className="text-sm text-gray-500 mt-1">For families getting started</p>
                      <div className="mt-4 flex items-baseline gap-1">
                        <span className="text-4xl font-bold text-gray-900">£0</span>
                        <span className="text-gray-500 text-sm">/ forever</span>
                      </div>
                    </div>

                    <Link
                      to="/signup"
                      className="block w-full btn btn-primary text-center mb-8"
                    >
                      Get started free
                    </Link>

                    <ul className="space-y-3">
                      {freeFeatures.map((feature) => (
                        <li key={feature} className="flex items-start gap-3">
                          <CheckIcon className="h-5 w-5 flex-shrink-0 text-gray-400 mt-0.5" />
                          <span className="text-sm text-gray-600">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* KinCare Family card */}
                  <div className="bg-primary-600 rounded-2xl p-8 shadow-xl relative">
                    {/* Most popular badge */}
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="inline-block bg-accent-300 text-accent-700 text-xs font-semibold px-4 py-1 rounded-full shadow-sm">
                        Most popular
                      </span>
                    </div>

                    <div className="mb-6">
                      <h2 className="text-xl font-bold text-white">KinCare Family</h2>
                      <p className="text-sm text-primary-100 mt-1">For families with bigger needs</p>
                      <div className="mt-4 flex items-baseline gap-1">
                        {billingCycle === 'monthly' ? (
                          <>
                            <span className="text-4xl font-bold text-white">£5.99</span>
                            <span className="text-primary-100 text-sm">/ month</span>
                          </>
                        ) : (
                          <>
                            <span className="text-4xl font-bold text-white">£4.00</span>
                            <span className="text-primary-100 text-sm">/ month</span>
                          </>
                        )}
                      </div>
                      {billingCycle === 'annual' && (
                        <p className="text-primary-100 text-xs mt-1">Billed as £47.99/year</p>
                      )}
                    </div>

                    <Link
                      to="/signup"
                      className="block w-full text-center bg-white text-primary-700 font-medium px-6 py-2.5 rounded-lg hover:bg-cream-100 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white mb-8"
                    >
                      Start free
                    </Link>

                    <ul className="space-y-3">
                      {familyFeatures.map((feature) => (
                        <li key={feature} className="flex items-start gap-3">
                          <CheckIcon className="h-5 w-5 flex-shrink-0 text-primary-100 mt-0.5" />
                          <span className="text-sm text-white">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Small print */}
                <p className="text-center text-sm text-gray-500 mt-6">
                  No credit card required. Cancel anytime.
                </p>
              </>
            )}
          </div>
        </section>

        {/* ---------------------------------------------------------------
            FAQ
        --------------------------------------------------------------- */}
        <section className="py-16 sm:py-20 lg:py-24 bg-white">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 text-center mb-12">
              Common questions
            </h2>
            <div>
              {faqs.map((faq) => (
                <FaqItem key={faq.question} question={faq.question} answer={faq.answer} />
              ))}
            </div>
          </div>
        </section>

        {/* ---------------------------------------------------------------
            Bottom CTA
        --------------------------------------------------------------- */}
        <section className="py-16 sm:py-20 lg:py-24 bg-gradient-to-br from-primary-600 to-primary-700">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white">
              Ready to coordinate your family's care?
            </h2>
            <p className="mt-4 text-lg text-primary-100">
              Join families who have found calm in the chaos.
            </p>
            <Link
              to="/signup"
              className="inline-block mt-8 bg-white text-primary-700 font-medium px-8 py-3 rounded-lg hover:bg-cream-100 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white"
            >
              Get Started Free
            </Link>
          </div>
        </section>
      </main>

      {/* -----------------------------------------------------------------
          Footer
      ----------------------------------------------------------------- */}
      <footer className="bg-gray-900 text-gray-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {/* Brand */}
            <div className="col-span-2 md:col-span-1">
              <span className="font-fraunces text-xl font-light tracking-tight text-white">
                <em className="font-medium text-primary-300" style={{ fontStyle: 'italic' }}>Kin</em>Care
              </span>
              <p className="mt-2 text-sm">Care together.</p>
              <p className="mt-4 text-xs text-gray-500">
                &copy; {new Date().getFullYear()} KinCare. All rights reserved.
              </p>
            </div>

            {/* Product */}
            <div>
              <h4 className="text-sm font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/#features" className="hover:text-white transition-colors">
                    Features
                  </Link>
                </li>
                <li>
                  <Link to="/#how-it-works" className="hover:text-white transition-colors">
                    How It Works
                  </Link>
                </li>
                <li>
                  <Link to="/pricing" className="hover:text-white transition-colors">
                    Pricing
                  </Link>
                </li>
                <li>
                  <Link to="/login" className="hover:text-white transition-colors">
                    Sign In
                  </Link>
                </li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="text-sm font-semibold text-white mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/privacy" className="hover:text-white transition-colors">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/cookies" className="hover:text-white transition-colors">
                    Cookie Policy
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="hover:text-white transition-colors">
                    Terms of Service
                  </Link>
                </li>
              </ul>
            </div>

            {/* Connect */}
            <div>
              <h4 className="text-sm font-semibold text-white mb-4">Connect</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <a href="mailto:hello@kin.care" className="hover:text-white transition-colors">
                    Contact Us
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
